import { ICancelablePromise } from '@Core/Animation/SpineWrapper';
import { TrackEntry } from '@esotericsoftware/spine-pixi-v8';
import EventsCenter from '@Managers/Events/EventsCenter';
import ParticleEmitter, { TParticleEmitterConfig } from './Elements/ParticleEmitter';
import { WinPresentation } from './WinPresentation';
import { EWaitFor, IWinPresentationStep, IWinPresentationStepExecutor } from './WinPresentation.types';

export class WinPresentationStepExecutor implements IWinPresentationStepExecutor {
    constructor(
        private readonly winPresentation: WinPresentation,
        private readonly createParticleEmitter: (configs: TParticleEmitterConfig[]) => ParticleEmitter | undefined
    ) {}

    private readonly effectHandlers: Record<string, (step: IWinPresentationStep) => void> = {
        eventToEmit: ({ eventToEmit }: IWinPresentationStep) => {
            if (!eventToEmit) return;
            EventsCenter.emit(eventToEmit);
        },

        stopSkipping: ({ stopSkipping }: IWinPresentationStep) => {
            if (!stopSkipping) return;
            this.winPresentation.winPresentationPromises.skipTriggered.reset();
        },

        particleEmitter: ({ particleEmitterConfigs }: IWinPresentationStep) => {
            if (!particleEmitterConfigs || particleEmitterConfigs.length === 0) return;
            this.createAndStartParticleEmitter(particleEmitterConfigs);
        },

        odometerFont: ({ odometer }: IWinPresentationStep) => {
            const font = odometer?.font;
            if (!font) return;
            this.winPresentation.odometer.setFont(font.key, font.size);
        },

        odometerTween: ({ odometer }: IWinPresentationStep) => {
            const tween = odometer?.tween;
            if (!tween) return;
            this.winPresentation.odometer.start({
                targetValue: tween.targetValue,
                duration: tween.duration,
                checkpoints: tween.checkpoints
            });
        },

        timer: ({ timer }: IWinPresentationStep) => {
            if (!timer) return;
            this.winPresentation.winPresentationPromises.startTimer(timer.duration);
        }
    };

    async execute(step: IWinPresentationStep): Promise<void> {
        this.applyStepEffects(step);
        await this.waitFor(step);
    }

    private applyStepEffects(step: IWinPresentationStep): void {
        Object.values(this.effectHandlers).forEach((apply) => apply(step));
    }

    private createAndStartParticleEmitter(configs: TParticleEmitterConfig[]): void {
        const emitter = this.createParticleEmitter(configs);
        emitter?.start();
    }

    private waitFor(step: IWinPresentationStep): Promise<void | TrackEntry> {
        const { waitFor, skippable } = step;

        let waitCondition: ICancelablePromise | Promise<void | TrackEntry>;
        let cancel: () => void = () => {};

        switch (waitFor) {
            case EWaitFor.WIN_PRESENTATION_START:
                waitCondition = this.winPresentation.winPresentationPromises.winStarted.promise;
                break;

            case EWaitFor.ANIMATION:
                ({ promise: waitCondition, cancel } = this.winPresentation.activeWinSpine.waitForAnimationEvent(
                    step.eventToEmit?.toString() ?? '',
                    'complete'
                ));
                break;

            case EWaitFor.ODOMETER:
                waitCondition = this.winPresentation.odometer.waitForTweenComplete();
                break;

            case EWaitFor.TIMER:
                waitCondition = this.winPresentation.winPresentationPromises.timerElapsed.promise;
                break;

            case EWaitFor.NONE:
            default:
                waitCondition = Promise.resolve();
        }

        if (!skippable) return waitCondition;

        return Promise.race([waitCondition, this.winPresentation.winPresentationPromises.skipTriggered.promise]).finally(() => cancel());
    }
}
